/**
 * SearchableDropdown Component Types
 *
 * A reusable dropdown component with integrated search functionality
 * for filtering dynamic API data locally.
 */

/**
 * Grouped items structure for dropdowns with category headers
 */
export interface GroupedItems<T> {
  groupLabel: string;
  items: T[];
  hasMore?: boolean; // Optional flag to indicate if more items are available for this group
}

/**
 * Props for the SearchableDropdown component
 */
/**
 * Result from async fetch function
 */
export interface FetchItemsResult<T> {
  items: T[] | GroupedItems<T>[];
  hasMore?: boolean; // For flat items, indicates if more are available
}

export interface SearchableDropdownProps<T> {
  // Data
  /** Array of items or grouped items to display in the dropdown (required if fetchItems not provided) */
  items?: T[] | GroupedItems<T>[];
  /** Whether data is currently being loaded */
  loading?: boolean;

  // Async fetching
  /** Function to fetch items asynchronously. Returns items and hasMore flag. */
  fetchItems?: (
    searchTerm: string,
    limit: number,
    offset: number
  ) => Promise<FetchItemsResult<T>>;
  /** Callback when "Load More" is clicked. Receives current search term. */
  onLoadMore?: (searchTerm: string) => void;
  /** Whether more items are available to load (for flat items) */
  hasMore?: boolean;
  /** Initial limit for fetching items (default: 20) */
  initialLimit?: number;

  // Display
  /** Custom trigger element (typically a Button) */
  trigger: React.ReactNode;
  /** Placeholder text for the dropdown trigger */
  placeholder?: string;
  /** Message to show when no data is available */
  emptyMessage?: string;
  /** Message to show when search returns no results */
  noResultsMessage?: string;

  // Search configuration
  /** Minimum number of characters required to start filtering (default: 1) */
  minSearchLength?: number;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Function to extract searchable text from an item */
  getSearchableText: (item: T) => string;

  // Rendering
  /** Function to render each dropdown item */
  renderItem: (item: T) => React.ReactNode;
  /** Function to get unique key for each item */
  getItemKey: (item: T) => string | number;

  // Selection
  /** Callback when an item is selected */
  onSelect: (item: T) => void;
  /** Currently selected item key (for highlighting) */
  selectedKey?: string | number;
  /** Whether the dropdown is disabled */
  disabled?: boolean;

  // Styling
  /** Additional CSS classes for the dropdown container */
  className?: string;
  /** Additional CSS classes for the dropdown content */
  dropdownClassName?: string;
  /** Maximum height for the dropdown content (default: "max-h-60") */
  maxHeight?: string;
}

/**
 * Type guard to check if items are grouped
 */
export function isGroupedItems<T>(
  items: T[] | GroupedItems<T>[]
): items is GroupedItems<T>[] {
  if (!Array.isArray(items)) {
    return false;
  }

  // Empty array cannot be determined as grouped or flat
  // But we'll treat it as not grouped for safety
  if (items.length === 0) {
    return false;
  }

  // Check if first item has the grouped structure
  const firstItem = items[0];
  if (
    firstItem &&
    typeof firstItem === "object" &&
    firstItem !== null &&
    "groupLabel" in firstItem &&
    "items" in firstItem &&
    Array.isArray((firstItem as any).items)
  ) {
    return true;
  }

  return false;
}
